/**
 * Canonical dropdown option sets for the quotes module.
 *
 * Values are the EXACT strings V1 stores in the quote.* tables — verified
 * 2026-06-11 against DISTINCT values in testquote (110k masterquote / 157k
 * quoterevisions / 113k directquote rows) and the V1 field definitions in
 * quote-dev/defs_quote.pl. Do not invent values here: V1 consumers
 * (cms-dev invoicing, fuel automation, storage billing) match on these
 * strings at runtime.
 *
 * Components should render stored legacy values not in these lists by
 * prepending the current value (see `withCurrent`) rather than dropping it.
 */

/**
 * #13 — Azure AD group whose members may bypass the minimum-rate floor (V1
 * bypassminimumrate ACL). FastCMS has no feature-permission slot, so the bypass
 * keys on group membership. LEFT EMPTY ON PURPOSE: the below-min Save gate stays
 * DORMANT (blocks no one) until ops fills in the real group id here; once set,
 * the gate hard-blocks below-min saves for users NOT in this group.
 * TODO(ops): set to the "GWI360 Quote Pricing" (or equivalent) AD group id.
 */
export const QUOTE_BYPASS_MINIMUM_RATE_GROUP_ID = '';

/** masterquote.fuelcalctype — defs_quote.pl:169 */
export const FUEL_CALC_TYPES = [
  'DELIVERY DATE',
  'PICKUP DATE',
  'CREATE DATE',
  'CURRENT DATE',
  'TERMINATE DATE',
];

/** masterquote.rateapplies — defs_quote.pl:191 */
export const RATE_APPLIES = ['ON DELIVERY', 'ON PICKUP', 'ON CREATE'];

/**
 * quoterevisions.businesstype ("Rev. Reason") — defs_quote.pl:197.
 * Live data also contains legacy values (OTHER, GRI, BAD INFO - INTERNAL,
 * BAD INFO - EXTERNAL); those render via `withCurrent`, not as new choices.
 */
export const REV_REASONS = [
  'INFO UPDATE',
  'QUOTING MISTAKE',
  'RATE REVISION',
  'RENEWAL',
  'INLAND RATE REVISION',
];

/** quoterevisions.status — defs_quote.pl:213 */
export const QUOTE_STATUSES = ['UNSECURED', 'SECURED'];

/** quoterevisions.volumetype — defs_quote.pl:215 */
export const VOLUME_TYPES = ['YEAR', 'MONTH', 'WEEK', 'DAY'];

/** quoterevisions.soldby — defs_quote.pl:207 */
export const SOLD_BY = ['BUSINESS DEVELOPMENT', 'INSIDE SALES'];

/** quoteadditionalcharges.freetimeunit — the Free Time unit dropdown. */
export const FREE_TIME_UNITS = ['DAYS', 'HOURS'];

/** directquote/transloadquote.movetype — defs_quote.pl:298,419 */
export const MOVE_TYPES = ['IMPORT', 'EXPORT', 'DOMESTIC', 'REPO'];

/**
 * Container sizes — defs_quote.pl:307,423. '20/40' is the single most common
 * stored value (91k rows); it was missing from the previous hardcoded list.
 */
export const SIZES = ['20', '20/40', '40', '45', '48', '53'];

/** directquote/transloadquote.equiptype — defs_quote.pl:283,405 */
export const EQUIP_TYPES = [
  'DRY',
  'FLATBED',
  'FLATRACK',
  'ISO TANK',
  'LTL',
  'OPENTOP',
  'OTHER',
  'RAILCAR',
  'REEFER',
  'VAN',
];

/** directquote.deliveryzone / deliveryquote.delratezone — LOCAL or OTR only */
export const DELIVERY_ZONES = ['LOCAL', 'OTR'];

/**
 * directquote.fsctype / deliveryquote.delfsctype — defs_quote.pl:407.
 * Stored value for the "GULF WINDS (CURRENT)" label is 'CURRENT' (76k rows).
 */
export const FSC_TYPES = [
  'CURRENT',
  'CUSTOMER',
  'FLAT',
  'MILES',
  'PCT%',
  'DISCOUNTED',
];

/** transloadquote.fueltype — same set plus warehouse-specific option */
export const FUEL_TYPES_WAREHOUSE = [...FSC_TYPES, 'WAREHOUSE FSC'];

/**
 * quoteadditionalcharges.addlchargeratetype + handlingquote.handlingratetype
 * — defs_quote.pl:239,362. (Blank = charge not applicable.)
 */
export const CHARGE_RATE_TYPES = ['EXTRA', 'EXTRA IN RATE', 'IN RATE', 'ON INVOICE'];

/**
 * `quoteadditionalcharges.additionaltype` — the per-charge "Additional", which is the
 * UNIT the charge bills in and therefore the invoice line's `liratetype`.
 *
 * V1's defs_quote.pl:273 list is the first four; it was a chassisfeetype list before
 * the column was reused per charge, which is where the name comes from. PER HOUR is
 * ours, added for WAITING TIME (Lee, 2026-10-21) — V1 has no per-hour chassis fee, so
 * it could not have been in that list. EACH is ours too: a per-piece charge, which
 * "+ Add to Invoice" stages as a FLAT line whose quantity is the count.
 *
 * Blank is a valid stored value and means FLAT; the engine defaults it that way
 * (`chassis_deps._DEFAULT_ADDITIONAL_FEETYPE`) and so does the Invoicing tab's
 * "+ Add to Invoice" (`quoteChargeLineShape`). The column is varchar(10), so every
 * member here has to fit in ten characters.
 */
export const CHASSIS_FEE_TYPES = ['FLAT', 'PER DAY', 'PER HOUR', 'MIN', 'MAX', 'EACH'];

/** chassistype — defs_quote.pl:386-388 */
export const CHASSIS_TYPES = [
  'COMBO',
  'DOUBLE DROP',
  'FLATBED',
  'MAFI',
  'SINGLE DROP',
  'SLIDER',
  'STANDARD',
  'SUPER LIGHT',
  'TRIAXLE',
  'ULTRA LIGHT',
];

/**
 * Rate zones are a cms.ratezones lookup (ratezone/displayname/region) — a
 * lookup endpoint is planned; until then this is the set of zones active in
 * live data so existing quotes render and new picks are real.
 * TODO(quotes-parity): replace with GET ratezones lookup.
 */
export const RATE_ZONES = [
  'BBC / BAYPORT',
  'BBC',
  'BAYPORT',
  'LOCAL-RAIL',
  'LOCAL-EAST',
  'HOUSTON',
  'DALLAS',
  'FTW / ALLIANCE',
  'MEMPHIS - BNSF',
  'MEMPHIS - CN/CSX',
  'MEMPHIS - NS',
  'SAVANNAH',
  'BALTIMORE',
  'CHARLESTON',
  'MOBILE',
  'NORFOLK',
  'UP MARION',
  'OTHER',
];

/* ── WAREHOUSE-quote (transloadquote) value sets ───────────────────────── */

/** transloadquote.accounttype — defs_quote.pl:271 */
export const ACCOUNT_TYPES = ['STORAGE', 'TRANSLOAD'];

/** transloadquote.whsetype — defs_quote.pl:321 (+ legacy REMOVE EXCESS in data) */
export const WHSE_TYPES = [
  'ADD/REMOVE EXCESS',
  'FULL STRIP/STUFF',
  'OUT OF GAUGE',
  'RAIL TRANSLOAD',
  'REWORK',
];

/** transloadquote.packagetype — defs_quote.pl:304, top stored values */
export const PACKAGE_TYPES = [
  'UNITIZED < 8,000 LBS',
  'UNITIZED 8,000 - 12,000 LBS',
  'UNITIZED > 12,000 LBS',
  'HAND STACKED 0-999',
  'HAND STACKED 1,000-2,000',
  'MACHINERY',
  'BUNDLES',
  'BALES',
  'PAPER ROLLS',
  'STEEL COILS',
  'SUPERSACKS (NON-PALLETIZED)',
  'OTHER',
];

/** transloadquote.warehouse / deliveryquote.delwarehouse — defs_quote.pl:659
 *  `@warehouse_data`. V1 keeps ONLY the active warehouses in this list;
 *  retired ones (BRISBANE, GREENSPORT, PORT CROSSING, SOUTH LOOP, PORTWALL,
 *  UNDERWOOD, GWI12/GWI17) stay in historical rows and are surfaced only on
 *  a quote that already carries them (via withCurrent/dedupOpts). */
export const WAREHOUSES = [
  'BAYPORT',
  'BARBOURS CUT',
  'BARBOURS CUT/BAYPORT',
  'BAYPORT/CERES OOG',
  'CERES',
  'SENS',
  'OTHER',
];

/** transloadquote.storagetype — defs_quote.pl:308 (+ legacy FULL MONTH/PER WEEK/PER DAY) */
export const STORAGE_TYPES = [
  'ANNIVERSARY',
  'AVERAGE',
  'SPLIT MONTH',
  'MANUAL',
  'DO NOT BILL',
];

/** transloadquote.numunits — defs_quote.pl:302 */
export const NUM_UNITS = ['<10', '10-25', '>25'];

/** transloadquote.storagedonotbillreason — defs_quote.pl:656 (@donotbillreason_data) */
export const DO_NOT_BILL_REASONS = [
  'CHARITY',
  'CONTRACTED',
  'INVENTORY ADJUSTMENT',
  'REEFER',
  'OTHER',
];

/** transloadquote.storageunittype — UOM values active in data */
export const STORAGE_UNIT_TYPES = [
  'PLT',
  'PCS',
  'CRATE',
  'SUPER SACK',
  'BUNDLE',
  'CONTAINER',
  'UNIT',
  'ROLL',
  'BALE',
];

/** transloadquote.drayhandlingunittype — values active in data */
export const HANDLING_UNIT_TYPES = [
  'CONTAINER',
  'METRIC TON',
  'SHORT TON',
  '100 WEIGHT',
  'PLT',
  'RAILCAR',
  'VAN',
  'SUPER SACK',
];

/** deliveryquote.delequiptype — lane (highway) equipment, distinct from container EQUIP_TYPES */
export const LANE_EQUIP_TYPES = [
  'VAN',
  'FLATBED',
  'CONTAINER',
  'PNEUMATIC TRUCK',
  'RAILCAR',
  'SINGLE DROP',
  'DOUBLE DROP',
  'HOPPER RAILCAR',
];

/**
 * draytype / deldeliverytype / deloverloadtype — rate-type qualifier; the only
 * value V1 ever stores is FLAT (else blank).
 */
export const RATE_TYPE_QUALIFIERS = ['FLAT'];

/**
 * Prepend the currently stored value so legacy/retired values still render
 * in a select instead of silently blanking (and then being overwritten on
 * save with whatever the select falls back to).
 */
export const withCurrent = (current: string | undefined, options: string[]): string[] => {
  const cur = (current ?? '').trim();
  if (!cur || options.includes(cur)) return options;
  return [cur, ...options];
};
